import { useState } from 'react';
import { Link } from 'react-router-dom';
import { INSURANCE } from './mockData';
import { daysUntil, money } from './utils';
import { useInsurance } from './InsuranceContext';
import { SourceMarkers } from './primitives';

const fx = INSURANCE;
/** One countdown, computed from today, used wherever a reset is mentioned. */
const RESET_DAYS = daysUntil(fx.plans[0]?.resets ?? fx.today, fx.today);

/* ── 4.2 Opportunities for this visit — compact list (visual pass §6) ─── */
function OppMoney({ o }: { o: (typeof fx.opportunities)[number] }) {
  const pair = (lbl: string, val: string) => (
    <div className="ins-pair" style={{ textAlign: 'right' }}><div className="lbl">{lbl}</div><div className="val sm">{val}</div></div>
  );
  if (o.insurance != null && o.patient != null) {
    return <div style={{ display: 'flex', gap: 18 }}>{pair('Insurance', money(o.insurance))}{pair('Patient', money(o.patient))}</div>;
  }
  if (o.patientPays) return pair('Patient pays', o.patientPays);
  if (o.patient != null) return pair('Patient pays', money(o.patient));
  if (o.insurance != null) return pair('Insurance pays', money(o.insurance));
  return null;
}

export function Opportunities() {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [reasonFor, setReasonFor] = useState<string | null>(null);
  const shown = fx.opportunities.filter((o) => !dismissed.has(o.type));

  return (
    <section className="ins-card" aria-labelledby="ins-opp-h" style={{ padding: 0 }}>
      <div style={{ padding: '18px 20px 10px', display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
        <h2 className="ins-h2" id="ins-opp-h" style={{ fontSize: 18 }}>Opportunities for this visit</h2>
        <span className="ins-note" style={{ marginTop: 0 }}>
          Ordered by value · {fx.hiddenBySettings.length} hidden by{' '}
          <Link to="/settings/opportunities" style={{ color: 'var(--color-primary)' }}>your settings</Link>
        </span>
      </div>
      <div className="ins-rowlist" style={{ padding: '0 20px 6px' }}>
        {shown.map((o) => (
          <div className="ins-rowitem" key={o.type} style={{ flexWrap: 'wrap' }}>
            <div className="grow">
              <div className="ttl">{o.title}{o.focus ? <span className="ins-tag ins-tag-focus" style={{ marginLeft: 8 }}>Practice focus</span> : null}</div>
              <div className="sub">{o.why.replace('{resetDays}', String(RESET_DAYS))}</div>
              {o.cappedNote ? <div className="sub" style={{ color: 'var(--color-warning)' }}>{o.cappedNote}</div> : null}
              <div className="ins-srcs" style={{ marginTop: 6 }}><SourceMarkers sources={o.sources} factTitle={o.title} /></div>
            </div>
            <div style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
              <OppMoney o={o} />
              {reasonFor === o.type ? (
                <ReasonPicker
                  onPick={() => { setDismissed((s) => new Set(s).add(o.type)); setReasonFor(null); }}
                  onCancel={() => { setReasonFor(null); }}
                />
              ) : (
                <button type="button" className="ins-btn ins-btn-secondary" onClick={() => { setReasonFor(o.type); }}>Not now</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

const REASONS = ['Patient declined', 'Not clinically right', 'Already discussed', 'Other'];
function ReasonPicker({ onPick, onCancel }: { onPick: (r: string) => void; onCancel: () => void }) {
  return (
    <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
      <span className="ins-label">Reason</span>
      {REASONS.map((r) => (
        <button key={r} type="button" className="ins-btn ins-btn-secondary" style={{ minHeight: 30, padding: '4px 9px' }} onClick={() => { onPick(r); }}>{r}</button>
      ))}
      <button type="button" className="ins-link" onClick={onCancel}>Cancel</button>
    </div>
  );
}

/* ── 4.3 Estimate for the next visit ─────────────────────────────────── */
export function VisitEstimate() {
  const e = fx.visitEstimate;
  return (
    <section className="ins-card" aria-labelledby="ins-est-h" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: '20px 24px 6px', display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
        <h2 className="ins-h2" id="ins-est-h" style={{ fontSize: 18 }}>Estimate for Wednesday’s visit</h2>
        <span className="ins-note" style={{ marginTop: 0 }}>{e.planSummary}</span>
      </div>
      <div className="ins-pairs" style={{ padding: '10px 24px 20px' }}>
        <div className="ins-pair"><div className="lbl">Patient pays about</div><div className="val big">{money(e.totals.patient)}</div></div>
        <div className="ins-pair"><div className="lbl">Insurance</div><div className="val mid">{money(e.totals.insurance)}</div></div>
        <div className="ins-pair"><div className="lbl">Contract write-off</div><div className="val mid" style={{ color: 'var(--color-foreground-secondary)' }}>{money(e.totals.writeOff)}</div></div>
      </div>
      <div className="ins-table-wrap" style={{ border: 0, borderTop: '1px solid var(--color-border)', borderRadius: 0, boxShadow: 'none' }}>
        <table className="ins-table">
          <caption className="ins-sr-only">Procedures for Wednesday’s visit</caption>
          <thead>
            <tr><th scope="col">Procedure</th><th scope="col" className="num">Fee</th><th scope="col" className="num">Allowed</th><th scope="col" className="num">Insurance</th><th scope="col" className="num">Patient</th></tr>
          </thead>
          <tbody>
            {e.lines.map((l) => (
              <tr key={l.code}>
                <td>
                  <div className="strong">{l.code} {l.label}</div>
                  {l.note ? <div className="ins-sub">{l.note} <SourceMarkers sources={['historical_data']} factTitle={l.label} /></div> : null}
                </td>
                <td className="num">{money(l.fee)}</td>
                <td className="num">{money(l.allowed)}</td>
                <td className="num">{money(l.insurance)}</td>
                <td className="num strong">{money(l.patient)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ padding: '14px 24px 18px', background: 'var(--color-background)', borderTop: '1px solid var(--color-border)', display: 'flex', flexDirection: 'column', gap: 4 }}>
        {e.notes.map((n, i) => (
          <p className="ins-note" key={i} style={{ marginTop: 0, fontSize: i === e.notes.length - 1 ? 12.5 : 14, color: i === e.notes.length - 1 ? 'var(--color-muted-foreground)' : 'var(--color-foreground-secondary)' }}>
            {n.text} {n.sources ? <SourceMarkers sources={n.sources} factTitle={n.text} /> : null}
          </p>
        ))}
      </div>
    </section>
  );
}

/* ── 4.4 Needs attention — open only, resolved collapsed (visual pass §6) ─ */
export function NeedsAttention({ onOpen }: { onOpen?: (target: string) => void }) {
  const { openPortal } = useInsurance();
  const [showResolved, setShowResolved] = useState(false);
  const open = fx.needsAttention.filter((n) => n.status === 'open');
  const resolved = fx.needsAttention.filter((n) => n.status === 'resolved_today');
  const act = (target: string) => { if (target === 'portal') openPortal(); else onOpen?.(target); };

  return (
    <section className="ins-card" aria-labelledby="ins-na-h" style={{ padding: 0 }}>
      <div style={{ padding: '18px 20px 6px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <h2 className="ins-h2" id="ins-na-h" style={{ fontSize: 18 }}>Needs attention</h2>
        {open.length ? <span className="ins-tag ins-tag-open">{open.length} open</span> : null}
      </div>
      <div className="ins-rowlist" style={{ padding: '0 20px' }}>
        {open.map((n) => (
          <div className="ins-rowitem" key={n.id}>
            <span className="ins-dot" style={{ background: 'var(--color-warning)' }} />
            <div className="grow"><div className="ttl">{n.title}</div><div className="sub">{n.detail}</div></div>
            <button type="button" className="ins-btn ins-btn-secondary" onClick={() => { act(n.viewTarget); }}>Open</button>
          </div>
        ))}
      </div>
      {resolved.length ? (
        <div style={{ borderTop: '1px solid var(--color-border)', padding: '12px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="ins-note" style={{ marginTop: 0, flex: 1 }}>{resolved.length} resolved today — {resolved[0]?.title.replace(/^Sealants/, 'sealants')}</span>
            <button type="button" className="ins-btn ins-btn-secondary" style={{ minHeight: 32 }} onClick={() => { setShowResolved((s) => !s); }} aria-expanded={showResolved}>{showResolved ? 'Hide' : 'Show'}</button>
          </div>
          {showResolved ? resolved.map((n) => (
            <div key={n.id} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <span className="ins-dot" style={{ background: 'var(--color-success)' }} />
              <div className="grow"><div className="ttl">{n.title}</div><div className="sub">{n.detail}</div></div>
            </div>
          )) : null}
        </div>
      ) : null}
    </section>
  );
}
